import { Divider, PageHeaderNavigationTab, Tabs } from "@cdkglobal/radial";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useIntl } from "react-intl";
import { useUIStateContext } from "../../../../contexts/UIStateContext";
import { useUserContext } from "../../../../contexts/UserContext";
import { useRefetchRecordData } from "../../../../hooks";
import { RecordPageProps, RecordPageRenderContext } from "../../../../types";
import {
  getRecordPageContainer,
  RecordPageMultiSectionLayout,
} from "../../../../utils/styled";
import ProgressBar from "../../../common/ProgressBar";
import Toaster from "../../../common/Toaster";
import { DEFAULT_TOAST } from "../../constants";
import {
  evaluateSchemaContext,
  getSetupInformation,
  getTabLabel,
  isAPICallRequiredForFetchingRowData,
} from "../../helpers";
import { resolveCustomStyle } from "../../style-config";
import {
  dispatchDashboardTabbedTabChange,
  dispatchSingleFieldOrSingleSetupRowdata,
} from "./recordPage.helpers";
import { renderSetupInfo } from "./recordPage.info";
import {
  buildTabItems,
  renderAdditionalSections,
} from "./recordPage.renderers";
import { renderSection } from "./recordPage.section";

export default function RecordPage({
  rowDataReducerState,
  dispatch,
}: Readonly<RecordPageProps>): JSX.Element {
  const intl = useIntl();
  const { t, i18n } = useTranslation("sectionTitles");

  const { schema, rowData, rowDataList, resetState } = rowDataReducerState;
  const { setupBaseRequestInfo, selected, permissions } = useUserContext();

  const {
    recordPageToast,
    isRecordOpen,
    isSideSheetOpen,
    setIsRecordOpen,
    setRecordPageToast,
    setLandingPageToast,
    activeTabIndex,
    setActiveTabIndex,
    selectedTabSectionKey,
    setSelectedTabSectionKey,
    selectedDashboardTabSectionKey,
    setSelectedDashboardTabSectionKey,
    setSearchValue,
  } = useUIStateContext();

  const {
    sections,
    tabKeys,
    activeTabKey,
    effectiveGridFields,
    setupMode,
    shouldRenderSetupTable,
  } = evaluateSchemaContext({
    schema,
    activeTabIndex,
    selectedDashboardTabSectionKey,
    isRecordOpen,
  });

  const { title, description } = getSetupInformation({
    selectedDashboardTabSectionKey,
    selectedSetupLabel: selected.setup.label,
    schemaDescription: schema.description,
    t,
    language: i18n.language,
  });

  const { refetch: refetchRecordData, isFetching: isRecordDataFetching } =
    useRefetchRecordData({
      rowData,
      setupBaseRequestInfo,
      setIsRecordOpen,
      setLandingPageToast,
      dispatch,
    });

  const ctx: RecordPageRenderContext = {
    schema,
    rowDataReducerState,
    dispatch,
    selected,
    isRecordOpen,
    isSideSheetOpen,
    shouldRenderSetupTable,
    effectiveGridFields,
    setupMode,
    title,
    description,
    t,
    language: i18n.language,
    setRecordPageToast,
    selectedTabSectionKey,
    selectedDashboardTabSectionKey,
  };

  const handleTabChange = (index: number) => {
    const nextTabKey = tabKeys[index];
    setActiveTabIndex(index);
    setSearchValue("");

    if (setupMode === "DASHBOARD_TABBED_SETUP") {
      dispatchDashboardTabbedTabChange({
        nextTabKey,
        index,
        rowData,
        rowDataList,
        resetState,
        isRecordOpen,
        dispatch,
        setSelectedDashboardTabSectionKey,
        isReadOnly: !permissions.setupPermission.hasManage,
      });
      return;
    }

    if (
      setupMode === "NORMAL_TAB_SETUP" ||
      setupMode === "INVENTORY_BUILDINGS_SETUP"
    ) {
      return;
    }

    setSelectedTabSectionKey(nextTabKey);
  };

  useEffect(() => {
    if (
      setupMode === "SINGLE_FIELD_DASHBOARD_LEVEL_SETUP" ||
      setupMode === "SINGLE_RECORD_DASHBOARD_LEVEL_SETUP"
    ) {
      dispatchSingleFieldOrSingleSetupRowdata({
        dispatch,
        rowDataList,
        isReadOnly: !permissions.setupPermission.hasManage,
      });
    }

    if (!tabKeys.length || !activeTabKey) return;

    if (setupMode === "DASHBOARD_TABBED_SETUP") {
      dispatchDashboardTabbedTabChange({
        nextTabKey: activeTabKey,
        index: activeTabIndex,
        rowData,
        rowDataList,
        resetState,
        isRecordOpen,
        dispatch,
        setSelectedDashboardTabSectionKey,
        isReadOnly: !permissions.setupPermission.hasManage, 
      });
    } else if (
      setupMode === "NORMAL_TAB_SETUP" ||
      setupMode === "INVENTORY_BUILDINGS_SETUP"
    ) {
      return;
    } else {
      setSelectedTabSectionKey(activeTabKey);
    }

    if (isAPICallRequiredForFetchingRowData(rowData, schema.title)) {
      refetchRecordData();
    }
  }, []);


  const RecordPageContainer = getRecordPageContainer(
    resolveCustomStyle({
      applicability: "record-page-container",
      selectedSetup: selected.setup.value,
    }),
  ) as React.ComponentType<React.HTMLAttributes<HTMLDivElement>>;

  const toast = recordPageToast.message.length > 0 && (
    <Toaster
      variant={recordPageToast.variant}
      message={recordPageToast.message}
      clearMessage={() => setRecordPageToast(DEFAULT_TOAST)}
      position="fixed"
      noActionButton={recordPageToast.variant === "neutral"}
      actionText={intl.formatMessage({
        id: "action.dismiss",
        defaultMessage: "Dismiss",
      })}
    />
  );

  if (isRecordDataFetching) {
    return (
      <RecordPageContainer>
        <ProgressBar
          dataTestId="indeterminate"
          label="Fetching..."
          loaderSize="large"
          trackWidth="500px"
          style={{ marginTop: "1rem" }}
        />
      </RecordPageContainer>
    );
  }

  if (setupMode === "INVENTORY_BUILDINGS_SETUP") {
    const activeTabKey = tabKeys[activeTabIndex];
    const activeSection = schema.sections?.[activeTabKey];

    return (
      <RecordPageContainer>
        {toast}

        {/* Tabs */}
        {tabKeys.map((tabKey, index) => (
          <PageHeaderNavigationTab
            key={tabKey}
            id={`inventory-tab-${tabKey}`}
            data-testid={`inventory-tab-${tabKey}`}
            isActive={activeTabIndex === index}
            label={getTabLabel(tabKey, ctx)}
            onClick={() => handleTabChange(index)}
            style={{ marginLeft: "-0.5rem" }}
          />
        ))}

        <Divider />

        {/* Active Tab Content */}
        <>
          {renderSetupInfo(ctx)}
          {renderAdditionalSections(ctx, "top", activeTabKey)}
          {renderSection(activeTabKey, activeSection, ctx)}
          {renderAdditionalSections(ctx, "bottom", activeTabKey)}
        </>
      </RecordPageContainer>
    );
  }

  if (tabKeys.length > 0) {
    return (
      <RecordPageContainer>
        {toast}
        <Tabs
          activeItemIndex={activeTabIndex}
          alignment="justified"
          dataTestId="tab-group"
          tabsLabel="Select a tab to edit"
          onActiveItemChange={handleTabChange}
          items={buildTabItems({
            tabKeys,
            schemaSections: schema.sections,
            ctx,
          })}
        />
      </RecordPageContainer>
    );
  }

  return (
    <RecordPageContainer>
      {toast}
      {renderSetupInfo(ctx)}
      <RecordPageMultiSectionLayout
        $customStyle={resolveCustomStyle({
          applicability: "multi-section",
          selectedSetup: selected.setup.value,
        })}
      >
        {renderAdditionalSections(ctx, "top")}
        {sections.map(({ sectionKey, sectionValue }) =>
          renderSection(sectionKey, sectionValue, ctx),
        )}
        {renderAdditionalSections(ctx, "bottom")}
      </RecordPageMultiSectionLayout>
    </RecordPageContainer>
  );
}
